import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchMastersWithStatus } from "../../apiServices/partsCatalogStatusApi";
import { masterAssemblyApi } from "../../apiServices/masterassembly";
import { useAuthoringStore } from "../../store/UseAuthorStore";
import { useUserStore } from "../../store/userStore";
import "./EditPartnumber.css";

const normalizeMediaPath = (path: string) => {
  const p = String(path || "").trim();
  if (!p) return "";
  const mediaIdx = p.indexOf("/media/");
  if (mediaIdx >= 0) {
    return p.slice(mediaIdx + "/media/".length);
  }
  if (p.startsWith("media/")) return p.slice("media/".length);
  if (p.startsWith("/media/")) return p.slice("/media/".length);
  return p;
};

const EditPartnumber: React.FC = () => {
  const { masterAssemblyId } = useParams();
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const role = String(user?.role || "OEM").toUpperCase();

  const basePath = process.env.REACT_APP_BASE_NAME || "";
  const oemFromBase = basePath.split("/")[1] || "";
  const oemFromUser = user?.oemId ? `Oem${user.oemId}` : "";
  const oem = oemFromBase || oemFromUser || "Oem1";

  const {
    setMasterAssembly,
    setPartDescription,
    setPartRevision,
    setMainSvg,
    buildAddPayload,
    masterAssembly,
  } = useAuthoringStore();

  const [partNumber, setPartNumber] = useState("");
  const [version, setVersion] = useState("");
  const [createdOn, setCreatedOn] = useState("");
  const [modifiedOn, setModifiedOn] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      setMessage(null);
      try {
        const list = await fetchMastersWithStatus(oem, role);
        const targetId = Number(masterAssemblyId);
        const item = list.items.find((i) => i.masterAssemblyId === targetId);
        if (!item?.componentPartNumber) {
          throw new Error("Component not found");
        }
        const data = await masterAssemblyApi.getByOEMAndPartNumber(oem, item.componentPartNumber);
        if (!mounted) return;
        setMasterAssembly(data);
        setPartNumber(data.componentPartNumber || "");
        setVersion(String(data.currentVersionId ?? ""));
        setCreatedOn(data.createdOn || "");
        setModifiedOn(data.modifiedOn || "");
        setDescription(String(data.componentDescription || "").toUpperCase());
        const imgPath = String(data?.mainSvg?.folderPath || "").trim();
        const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
        if (!imgPath) {
          setPreviewUrl("");
        } else if (/^https?:\/\//i.test(imgPath) || imgPath.includes("http://") || imgPath.includes("https://")) {
          const mediaIdx = imgPath.indexOf("/media/");
          if (mediaIdx >= 0) {
            const tail = imgPath.slice(mediaIdx + "/media/".length);
            setPreviewUrl(`${baseUrl}/media/${tail}`);
          } else {
            setPreviewUrl(imgPath);
          }
        } else if (imgPath.startsWith("/media/")) {
          setPreviewUrl(`${baseUrl}${imgPath}`);
        } else if (imgPath.startsWith("media/")) {
          setPreviewUrl(`${baseUrl}/${imgPath}`);
        } else {
          setPreviewUrl(`${baseUrl}/media/${imgPath}`);
        }
        if (data.currentVersionId != null) setPartRevision(String(data.currentVersionId));
      } catch (e: any) {
        if (mounted) setError(e?.message || "Failed to load component");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    if (masterAssemblyId) void load();
    return () => {
      mounted = false;
    };
  }, [masterAssemblyId, oem, role, setMasterAssembly, setPartRevision]);

  const handleSave = async () => {
    if (!masterAssembly?.masterAssemblyId) {
      setError("Missing master assembly.");
      return;
    }
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const upperDesc = description.trim().toUpperCase();
      setDescription(upperDesc);
      setPartDescription(upperDesc);

      if (file) {
        const svg = await masterAssemblyApi.uploadSvg({
          file,
          masterAssemblyId: masterAssembly.masterAssemblyId,
          figNumber: "main",
          oem,
        });
        if (!svg || !svg.svgId) {
          throw new Error("SVG upload failed");
        }
        setMainSvg({
          svgId: svg.svgId,
          folderPath: normalizeMediaPath(svg.folderPath),
          fileName: svg.fileName,
        });
      }

      const payload = buildAddPayload();
      payload.masterAssemblyId = masterAssembly.masterAssemblyId;
      payload.catalog = [];
      const resp = await masterAssemblyApi.updateMaster(oem, payload);
      if (resp?.statusCode === 200) {
        setMessage("✅ Updated successfully");
      } else {
        throw new Error(resp?.message || "Update failed");
      }
    } catch (e: any) {
      setError(e?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="edit-partnumber">
      <div className="edit-partnumber__card">
        <div className="edit-partnumber__header">
          <h2>Edit Component</h2>
        </div>

        <div className="edit-partnumber__body">
          <div className="edit-partnumber__column">
            <div className="edit-partnumber__field">
              <label>Part Number</label>
              <input value={partNumber} disabled />
            </div>
            <div className="edit-partnumber__field">
              <label>Version</label>
              <input value={version} disabled />
            </div>
            <div className="edit-partnumber__field">
              <label>Created Date</label>
              <input value={createdOn} disabled />
            </div>
            <div className="edit-partnumber__field">
              <label>Modified Date</label>
              <input value={modifiedOn} disabled />
            </div>
          </div>

          <div className="edit-partnumber__column">
            <div className="edit-partnumber__field">
              <label>Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value.toUpperCase())}
                placeholder="Update component description..."
              />
            </div>

            <div className="edit-partnumber__field">
              <label>Current Figure</label>
              {previewUrl ? (
                <div className="edit-partnumber__preview">
                  <img src={previewUrl} alt="Current figure" />
                </div>
              ) : (
                <div className="edit-partnumber__preview empty">No figure uploaded</div>
              )}
            </div>

            <div className="edit-partnumber__field">
              <label>Update Figure</label>
              <input
                type="file"
                accept=".svg,.png,.jpg,.jpeg"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              {file && <div className="edit-partnumber__filename">{file.name}</div>}
            </div>
          </div>
        </div>

        {error && <div className="edit-partnumber__message error">{error}</div>}
        {message && <div className="edit-partnumber__message success">{message}</div>}

        <div className="edit-partnumber__footer">
          <button type="button" className="edit-partnumber__btn outline" onClick={() => navigate(-1)}>
            Back
          </button>
          <button type="button" className="edit-partnumber__btn" onClick={handleSave} disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPartnumber;
